/**
 * Темы кабинета Рублокса (порт системы тем Маськи, theme/colors.js).
 *
 * Тема — набор CSS-переменных, накладываемых инлайном на корень `.rb-cab`
 * в CabinetLayout. Красится ТОЛЬКО внутренняя часть сайта (/app):
 * лендинг, студия и плеер не затрагиваются (у них свои стили).
 *
 * Цены и владение — на бэке (GET /cosmetics/themes, цены = Маська:
 * classic/dark бесплатно, ocean 20, rublox 40, sakura 60, forest 80,
 * sunset 100 алмазов). Здесь только палитры и метаданные.
 */

export const CAB_THEMES = {
  classic: {
    key: "classic", title: "Классика", isDark: false,
    // дефолтная светлая палитра кабинета — переопределений нет
    vars: {},
    // превью-градиент для свотча в настройках
    preview: "linear-gradient(135deg, #f5f7fb 50%, #3357ff 50%)",
  },
  dark: {
    key: "dark", title: "Тёмная", isDark: true,
    vars: {
      "--cab-bg": "#1a1a2e",
      "--cab-bg-soft": "#1f1f3a",
      "--cab-card": "#16213e",
      "--cab-dark-panel": "#0d1530",
      "--cab-text": "#e0e0e0",
      "--cab-text-2": "#b8bcd8",
      "--cab-text-muted": "#8a8aaa",
      "--cab-text-faint": "#7a7a9a",
      "--cab-border": "#2a2a5e",
      "--cab-accent-soft": "#0f3460",
      "--cab-blue": "#e94560",
      "--cab-blue-deep": "#c22c48",
    },
    preview: "linear-gradient(135deg, #1a1a2e 50%, #e94560 50%)",
  },
  ocean: {
    key: "ocean", title: "Океан", isDark: false,
    vars: {
      "--cab-bg": "#e8f4f8",
      "--cab-bg-soft": "#f0f8fb",
      "--cab-card": "#ffffff",
      "--cab-text": "#1a3c5e",
      "--cab-text-2": "#2a5478",
      "--cab-text-muted": "#6a94a8",
      "--cab-text-faint": "#8ab4c8",
      "--cab-border": "#b8d8e8",
      "--cab-accent-soft": "#e3f2fd",
      "--cab-blue": "#2196F3",
      "--cab-blue-deep": "#1565C0",
    },
    preview: "linear-gradient(135deg, #e8f4f8 50%, #2196F3 50%)",
  },
  rublox: {
    key: "rublox", title: "Рублокс", isDark: true,
    // фирменная тёмно-синяя с брендовым синим акцентом
    vars: {
      "--cab-bg": "#0b1226",
      "--cab-bg-soft": "#101a33",
      "--cab-card": "#131c36",
      "--cab-dark-panel": "#080d1c",
      "--cab-text": "#e6ecff",
      "--cab-text-2": "#b9c5ec",
      "--cab-text-muted": "#8b97c2",
      "--cab-text-faint": "#68739c",
      "--cab-border": "#22304f",
      "--cab-accent-soft": "#182448",
    },
    preview: "linear-gradient(135deg, #0b1226 50%, #3357ff 50%)",
  },
  sakura: {
    key: "sakura", title: "Сакура", isDark: false,
    vars: {
      "--cab-bg": "#fdf2f8",
      "--cab-bg-soft": "#fef6fa",
      "--cab-card": "#fff5f9",
      "--cab-text": "#4a2040",
      "--cab-text-2": "#6a3858",
      "--cab-text-muted": "#a87090",
      "--cab-text-faint": "#c890a8",
      "--cab-border": "#f0c0d8",
      "--cab-accent-soft": "#fce7f3",
      "--cab-blue": "#e91e8c",
      "--cab-blue-deep": "#ad1457",
    },
    preview: "linear-gradient(135deg, #fdf2f8 50%, #e91e8c 50%)",
  },
  forest: {
    key: "forest", title: "Лес", isDark: true,
    vars: {
      "--cab-bg": "#1b2e1b",
      "--cab-bg-soft": "#213821",
      "--cab-card": "#243524",
      "--cab-dark-panel": "#142414",
      "--cab-text": "#d4e8d0",
      "--cab-text-2": "#aacaa4",
      "--cab-text-muted": "#8aac86",
      "--cab-text-faint": "#7a9a78",
      "--cab-border": "#3a5a3a",
      "--cab-accent-soft": "#2d422d",
      "--cab-blue": "#66bb6a",
      "--cab-blue-deep": "#388e3c",
    },
    preview: "linear-gradient(135deg, #1b2e1b 50%, #66bb6a 50%)",
  },
  sunset: {
    key: "sunset", title: "Закат", isDark: true,
    vars: {
      "--cab-bg": "#2d1b3d",
      "--cab-bg-soft": "#35204a",
      "--cab-card": "#3d2550",
      "--cab-dark-panel": "#20122e",
      "--cab-text": "#f0d8ff",
      "--cab-text-2": "#d0b4e8",
      "--cab-text-muted": "#ab8ac2",
      "--cab-text-faint": "#9a78b0",
      "--cab-border": "#5a3870",
      "--cab-accent-soft": "#4a2d60",
      "--cab-blue": "#ff9800",
      "--cab-blue-deep": "#e65100",
    },
    preview: "linear-gradient(135deg, #2d1b3d 50%, #ff9800 50%)",
  },
};

const THEME_STORAGE_KEY = "rublox_app_theme";

/** Активная тема из localStorage (мгновенно, до загрузки профиля). */
export function getStoredTheme() {
  try {
    const k = localStorage.getItem(THEME_STORAGE_KEY);
    return k && CAB_THEMES[k] ? k : "classic";
  } catch {
    return "classic";
  }
}

export function storeTheme(key) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, CAB_THEMES[key] ? key : "classic");
  } catch {
    /* приватный режим */
  }
}

/** CSS-переменные темы для style корня .rb-cab. */
export function themeVars(key) {
  return (CAB_THEMES[key] || CAB_THEMES.classic).vars;
}

/** Тёмная ли тема. Нужно CSS-классу .is-dark: на тёмном фоне тени и
    заливки состояний подменяются целиком, а не масштабируются. */
export function isDarkTheme(key) {
  return !!(CAB_THEMES[key] || CAB_THEMES.classic).isDark;
}
